import { useState, useCallback } from "react";

interface OverlapCheckParams {
  startDate: Date | null;
  endDate: Date | null;
  authType: string;
  approvedUnits: any[];
  excludeId?: number | null;
}

const canAuthTypesOverlap = (authType1: string, authType2: string) => {
  if (authType1 === "secondary_auth" || authType2 === "secondary_auth") {
    const otherType = authType1 === "secondary_auth" ? authType2 : authType1;
    return otherType !== "secondary_auth";
  }

  if (authType1 === "pre_auth" || authType2 === "pre_auth") {
    const otherType = authType1 === "pre_auth" ? authType2 : authType1;
    return otherType === "secondary_auth";
  }

  if (authType1 === "re_auth" || authType2 === "re_auth") {
    const otherType = authType1 === "re_auth" ? authType2 : authType1;
    return otherType !== "re_auth";
  }

  if (authType1 === "auth" || authType2 === "auth") {
    const otherType = authType1 === "auth" ? authType2 : authType1;
    if (otherType === "auth") {
      return true;
    }
    return otherType === "re_auth" || otherType === "secondary_auth";
  }

  return false;
};

const hasOverlappingCPTCodes = (auth1: any, auth2: any) => {
  if (
    auth1.authorization_type === "secondary_auth" ||
    auth2.authorization_type === "secondary_auth"
  ) {
    return false;
  }

  if (!auth1.approvedUnits || !auth2.approvedUnits) {
    return false;
  }

  const auth1CodeIds = auth1.approvedUnits
    .map((unit: any) => unit.insurance_code_id || unit.codeId)
    .filter(Boolean);
  const auth2CodeIds = auth2.approvedUnits
    .map((unit: any) => unit.insurance_code_id || unit.codeId)
    .filter(Boolean);

  if (auth1CodeIds.length === 0 || auth2CodeIds.length === 0) {
    return false;
  }

  return auth1CodeIds.some((codeId: number) => auth2CodeIds.includes(codeId));
};

const shouldFilterAsOverlapping = (
  code: any,
  params: OverlapCheckParams
) => {
  const { startDate, endDate, authType, approvedUnits, excludeId } = params;

  const canOverlap = canAuthTypesOverlap(authType, code.authorization_type);
  const isNonOverlappingConflict = !canOverlap;

  let hasDateOverlap;
  if (isNonOverlappingConflict) {
    if (!startDate || !endDate || !code.start_date || !code.end_date) {
      return false;
    }

    try {
      const codeStartDate = new Date(code.start_date);
      const codeEndDate = new Date(code.end_date);
      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDate);

      if (
        isNaN(codeStartDate.getTime()) ||
        isNaN(codeEndDate.getTime()) ||
        isNaN(startDateObj.getTime()) ||
        isNaN(endDateObj.getTime())
      ) {
        return false;
      }

      const codeStartUTC = codeStartDate.toISOString().split("T")[0];
      const codeEndUTC = codeEndDate.toISOString().split("T")[0];
      const startDateUTC = startDateObj.toISOString().split("T")[0];
      const endDateUTC = endDateObj.toISOString().split("T")[0];

      hasDateOverlap =
        (codeStartUTC < endDateUTC && codeEndUTC > startDateUTC) ||
        codeStartUTC === endDateUTC ||
        codeEndUTC === startDateUTC;
    } catch {
      return false;
    }
  } else {
    hasDateOverlap =
      new Date(code.start_date) <= new Date(endDate!) &&
      new Date(code.end_date) >= new Date(startDate!);
  }

  if (!hasDateOverlap) {
    return false;
  }

  if (excludeId != null && code.id === excludeId) {
    return false;
  }

  if (!canOverlap) {
    return true;
  }

  if (authType === "auth" && code.authorization_type === "auth") {
    const authForCheck = { authorization_type: authType, approvedUnits };
    return hasOverlappingCPTCodes(authForCheck, code);
  }

  return false;
};

const getOverlapType = (authType: string, overlappingAuths: any[], approvedUnits: any[]) => {
  if (overlappingAuths.length === 0) {
    return null;
  }

  const hasAuthTypeConflict = overlappingAuths.some((auth) => {
    return !canAuthTypesOverlap(authType, auth.authorization_type);
  });

  if (hasAuthTypeConflict) {
    const hasSecondaryConflict =
      authType === "secondary_auth" &&
      overlappingAuths.some((auth) => auth.authorization_type === "secondary_auth");

    if (hasSecondaryConflict) {
      return "secondary_auth_conflict";
    }

    const hasPreAuthConflict = overlappingAuths.some(
      (auth) =>
        (authType === "pre_auth" &&
          (auth.authorization_type === "auth" || auth.authorization_type === "re_auth")) ||
        ((authType === "auth" || authType === "re_auth") &&
          auth.authorization_type === "pre_auth")
    );

    return hasPreAuthConflict ? "pre_auth_conflict" : "auth_type_conflict";
  }

  const authForCheck = { authorization_type: authType, approvedUnits };
  const hasCPTConflict = overlappingAuths.some((auth) =>
    hasOverlappingCPTCodes(authForCheck, auth)
  );

  return hasCPTConflict ? "cpt_conflict" : "general_overlap";
};

const getDialogContent = (authType: string, overlappingAuths: any[], approvedUnits: any[]) => {
  const overlapType = getOverlapType(authType, overlappingAuths, approvedUnits);

  switch (overlapType) {
    case "cpt_conflict":
      return {
        title: "Overlapping codes",
        message:
          "You already have an active therapy authorization with the same CPT codes during this time period. Proceeding will update the current Ongoing authorization to end one day prior to the start of this authorization period.",
      };
    case "secondary_auth_conflict":
      return {
        title: "Overlapping Secondary Authorization Periods",
        message:
          "The dates of this secondary authorization overlap an existing secondary authorization period. Proceeding will update the current secondary authorization to end one day prior to the start of this authorization period.",
      };
    case "pre_auth_conflict":
      return {
        title: "Conflicting authorization types",
        message:
          "Assessment authorizations cannot overlap with Ongoing authorizations or Re-authorizations. Proceeding will update the Assessment authorization to end one day prior to the start of the Ongoing authorization or Re-authorization.",
      };
    case "auth_type_conflict":
      return {
        title: "Conflicting Authorization Periods",
        message:
          "The dates of this Ongoing authorization overlap an existing Ongoing authorization period. Proceeding will update the current Ongoing authorization to end one day prior to the start of this authorization period.",
      };
    case "general_overlap":
    default:
      return {
        title: "Overlapping Ongoing Authorization Periods",
        message:
          "The dates of this Ongoing authorization overlap an existing Ongoing authorization period. Proceeding will update the current Ongoing authorization to end one day prior to the start of this authorization period.",
      };
  }
};

const useOverlapDetection = (authorizationCodes: any[]) => {
  const [overlapError, setOverlapError] = useState(false);
  const [overlappingAuths, setOverlappingAuths] = useState<any[]>([]);
  const [overlapDialogOpen, setOverlapDialogOpen] = useState(false);

  const checkForOverlaps = useCallback(
    (params: OverlapCheckParams) => {
      const overlapping = authorizationCodes.filter((code) =>
        shouldFilterAsOverlapping(code, params)
      );
      const hasError = overlapping.length > 0;
      setOverlapError(hasError);
      setOverlappingAuths(overlapping);
    },
    [authorizationCodes]
  );

  const resetOverlap = useCallback(() => {
    setOverlapError(false);
    setOverlappingAuths([]);
  }, []);

  const toggleOverlapDialog = useCallback(() => {
    setOverlapDialogOpen((prev) => !prev);
  }, []);

  const getOverlapDialogContent = useCallback(
    (authType: string, approvedUnits: any[]) => {
      return getDialogContent(authType, overlappingAuths, approvedUnits);
    },
    [overlappingAuths]
  );

  return {
    overlapError,
    overlappingAuths,
    overlapDialogOpen,
    setOverlapDialogOpen,
    checkForOverlaps,
    resetOverlap,
    toggleOverlapDialog,
    getOverlapDialogContent,
  };
};

export default useOverlapDetection;
